import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { loyaltyAPI } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Crown, Star, Award, Gift, TrendingUp, Clock, ChevronRight,
  Sparkles, CheckCircle, ArrowUpRight, ShoppingBag,
} from 'lucide-react';

const TIER_CONFIG = {
  bronze: { 
    color: '#CD7F32', 
    bg: 'rgba(205,127,50,0.1)', 
    icon: Award,
    label: 'Bronze',
  },
  silver: { 
    color: '#C0C0C0', 
    bg: 'rgba(192,192,192,0.15)', 
    icon: Star,
    label: 'Silver',
  },
  gold: { 
    color: '#C5A55A', 
    bg: 'rgba(197,165,90,0.15)', 
    icon: Crown,
    label: 'Gold',
  },
  platinum: { 
    color: '#E5E4E2', 
    bg: 'linear-gradient(135deg, rgba(229,228,226,0.2), rgba(180,180,180,0.1))', 
    icon: Sparkles,
    label: 'Platinum',
  },
};

const REWARD_TYPE_LABELS = {
  discount_percentage: 'Discount %',
  discount_flat: 'Flat Discount',
  free_service: 'Free Service',
  free_garment: 'Free Garment',
  experience: 'Experience',
};

export function LoyaltyCard({ clientId, compact = false }) {
  const [program, setProgram] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRewardsShop, setShowRewardsShop] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    Promise.all([
      loyaltyAPI.getProgram(clientId),
      loyaltyAPI.getTransactions(clientId, 5),
    ]).then(([progRes, txRes]) => {
      setProgram(progRes.data);
      setTransactions(txRes.data?.transactions || []);
    }).catch(() => {
      toast.error('Failed to load loyalty data');
    }).finally(() => setLoading(false));
  }, [clientId]);

  if (loading) {
    return (
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardContent className="p-4">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!program) return null;

  const tier = program.tier || 'bronze';
  const config = TIER_CONFIG[tier];
  const TierIcon = config.icon;
  
  const progressPercent = program.points_to_next_tier && program.next_tier
    ? Math.min(100, ((program.lifetime_points_earned - (program.lifetime_points_earned - program.points_to_next_tier)) / program.points_to_next_tier) * 100)
    : 100;

  if (compact) {
    return (
      <div 
        className="flex items-center gap-2 px-3 py-1.5 rounded-full"
        style={{ backgroundColor: config.bg }}
        title={`${program.total_points.toLocaleString()} points`}
      >
        <TierIcon className="w-3.5 h-3.5" style={{ color: config.color }} />
        <span className="text-xs font-semibold" style={{ color: config.color }}>
          {config.label}
        </span>
        <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>
          {program.total_points.toLocaleString()} pts
        </span>
      </div>
    );
  }

  return (
    <>
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm overflow-hidden" data-testid="loyalty-card">
        {/* Tier Banner */}
        <div 
          className="px-4 py-3 flex items-center justify-between"
          style={{ background: tier === 'platinum' ? config.bg : config.bg }}
        >
          <div className="flex items-center gap-2">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'white', boxShadow: `0 0 12px ${config.color}40` }}
            >
              <TierIcon className="w-5 h-5" style={{ color: config.color }} />
            </div>
            <div>
              <p className="text-sm font-bold" style={{ color: tier === 'platinum' ? '#333' : config.color }}>
                {config.label} Member
              </p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                Herringbone & Sui Loyalty
              </p>
            </div>
          </div>
          <Badge 
            className="text-xs border-0 px-3 py-1"
            style={{ backgroundColor: config.color, color: tier === 'silver' || tier === 'platinum' ? '#333' : 'white' }}
          >
            {program.total_points.toLocaleString()} pts
          </Badge>
        </div>

        <CardContent className="p-4 space-y-4">
          {/* Points & Progress */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>Lifetime Points</span>
              <span className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>
                {program.lifetime_points_earned?.toLocaleString() || 0}
              </span>
            </div>
            
            {program.next_tier && program.points_to_next_tier > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                    {program.points_to_next_tier.toLocaleString()} pts to {TIER_CONFIG[program.next_tier]?.label}
                  </span>
                  <ArrowUpRight className="w-3 h-3" style={{ color: TIER_CONFIG[program.next_tier]?.color }} />
                </div>
                <Progress value={100 - (program.points_to_next_tier / (program.lifetime_points_earned + program.points_to_next_tier) * 100)} className="h-1.5" />
              </div>
            )}
          </div>

          {/* Lifetime Stats */}
          <div className="grid grid-cols-3 gap-2">
            <div className="p-2.5 rounded-lg text-center" style={{ backgroundColor: 'rgba(34,197,94,0.06)' }}>
              <p className="text-xs font-bold text-green-600">{(program.lifetime_points_earned || 0).toLocaleString()}</p>
              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Total Earned</p>
            </div>
            <div className="p-2.5 rounded-lg text-center" style={{ backgroundColor: 'rgba(239,68,68,0.06)' }}>
              <p className="text-xs font-bold text-red-500">{(program.total_redeemed || Math.max(0, (program.lifetime_points_earned || 0) - (program.total_points || 0))).toLocaleString()}</p>
              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Redeemed</p>
            </div>
            <div className="p-2.5 rounded-lg text-center" style={{ backgroundColor: 'rgba(27,42,74,0.04)' }}>
              <p className="text-xs font-bold" style={{ color: 'var(--brand-navy)' }}>
                {program.created_at ? new Date(program.created_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : '—'}
              </p>
              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Member Since</p>
            </div>
          </div>

          {/* Tier Benefits */}
          {program.tier_benefits?.length > 0 && (
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
              <p className="text-[10px] font-semibold mb-2" style={{ color: 'var(--brand-navy)' }}>Your Benefits</p>
              <div className="space-y-1">
                {program.tier_benefits.slice(0, 3).map((benefit, i) => (
                  <div key={i} className="flex items-start gap-1.5">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: config.color }} />
                    <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Transactions */}
          {transactions.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold mb-2" style={{ color: 'var(--brand-navy)' }}>Recent Activity</p>
              <div className="space-y-1.5">
                {transactions.slice(0, 3).map(tx => (
                  <div key={tx.id} className="flex items-center justify-between text-[10px]">
                    <div className="flex items-center gap-1.5">
                      {tx.type === 'earned' ? (
                        <TrendingUp className="w-3 h-3 text-green-500" />
                      ) : tx.type === 'redeemed' ? (
                        <Gift className="w-3 h-3 text-amber-500" />
                      ) : tx.type === 'referral_bonus' ? (
                        <ArrowUpRight className="w-3 h-3 text-purple-500" />
                      ) : tx.type === 'bonus' ? (
                        <Sparkles className="w-3 h-3 text-blue-500" />
                      ) : (
                        <Clock className="w-3 h-3 text-gray-400" />
                      )}
                      <span className="truncate max-w-[140px]" style={{ color: 'var(--ink-muted)' }}>
                        {tx.description}
                      </span>
                    </div>
                    <span 
                      className="font-semibold"
                      style={{ color: tx.points > 0 ? '#16A34A' : '#DC2626' }}
                    >
                      {tx.points > 0 ? '+' : ''}{tx.points}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rewards Shop Button */}
          <Button 
            onClick={() => setShowRewardsShop(true)}
            className="w-full h-9 gap-2 text-xs"
            style={{ backgroundColor: 'var(--brand-gold)' }}
            data-testid="rewards-shop-btn"
          >
            <Gift className="w-3.5 h-3.5" />
            Rewards Shop
            <ChevronRight className="w-3.5 h-3.5 ml-auto" />
          </Button>
        </CardContent>
      </Card>

      {/* Rewards Shop Dialog */}
      <RewardsShopDialog 
        open={showRewardsShop} 
        onOpenChange={setShowRewardsShop}
        clientId={clientId}
        clientPoints={program.total_points}
        onRedeemed={() => {
          // Refresh data after redemption
          loyaltyAPI.getProgram(clientId).then(r => setProgram(r.data));
          loyaltyAPI.getTransactions(clientId, 5).then(r => setTransactions(r.data?.transactions || []));
        }}
      />
    </>
  );
}

function RewardsShopDialog({ open, onOpenChange, clientId, clientPoints, onRedeemed }) {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    loyaltyAPI.getRewards(true).then(res => {
      setRewards(res.data?.rewards || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, clientId]);

  const handleRedeem = async (reward) => {
    if (clientPoints < reward.points_required) {
      toast.error('Insufficient points');
      return;
    }
    
    setRedeeming(reward.id);
    try {
      await loyaltyAPI.redeemReward({
        client_id: clientId,
        reward_id: reward.id,
      });
      toast.success(`Redeemed: ${reward.name}`);
      onRedeemed?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem');
    } finally {
      setRedeeming(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh]" data-testid="rewards-shop-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Gift className="w-5 h-5" style={{ color: 'var(--brand-gold)' }} />
            Rewards Shop
          </DialogTitle>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            Your balance: <span className="font-bold" style={{ color: 'var(--brand-gold)' }}>{clientPoints?.toLocaleString()} points</span>
          </p>
        </DialogHeader>
        
        <ScrollArea className="max-h-[50vh] pr-2">
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : rewards.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: 'var(--ink-muted)' }}>
              No rewards available
            </p>
          ) : (
            <div className="space-y-3">
              {rewards.map(reward => {
                const canAfford = clientPoints >= reward.points_required;
                return (
                  <div 
                    key={reward.id}
                    className={`p-3 rounded-xl border ${canAfford ? 'border-border/60' : 'border-gray-200 opacity-60'}`}
                    style={{ backgroundColor: canAfford ? 'white' : 'rgba(0,0,0,0.02)' }}
                  >
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>
                          {reward.name}
                        </p>
                        <p className="text-[10px] mt-0.5 line-clamp-2" style={{ color: 'var(--ink-muted)' }}>
                          {reward.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[9px] px-2 py-0">
                            {REWARD_TYPE_LABELS[reward.reward_type] || reward.reward_type}
                          </Badge>
                          {reward.reward_value > 0 && (
                            <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                              {reward.reward_type === 'discount_percentage' ? `${reward.reward_value}% off` : `₹${reward.reward_value.toLocaleString()}`}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold" style={{ color: canAfford ? 'var(--brand-gold)' : 'var(--ink-muted)' }}>
                          {reward.points_required.toLocaleString()}
                        </p>
                        <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>points</p>
                        <Button
                          size="sm"
                          disabled={!canAfford || redeeming === reward.id}
                          onClick={() => handleRedeem(reward)}
                          className="mt-2 h-7 text-[10px] px-3"
                          style={{ backgroundColor: canAfford ? 'var(--brand-gold)' : undefined }}
                          data-testid={`redeem-btn-${reward.id}`}
                        >
                          {redeeming === reward.id ? 'Redeeming...' : canAfford ? 'Redeem' : 'Need more pts'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// Compact tier badge for lists
export function LoyaltyTierBadge({ tier, points }) {
  if (!tier) return null;
  const config = TIER_CONFIG[tier] || TIER_CONFIG.bronze;
  const TierIcon = config.icon;
  
  return (
    <div 
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full"
      style={{ backgroundColor: config.bg }}
      title={points ? `${points.toLocaleString()} points` : undefined}
    >
      <TierIcon className="w-3 h-3" style={{ color: config.color }} />
      <span className="text-[10px] font-medium" style={{ color: config.color }}>
        {config.label}
      </span>
    </div>
  );
}

export default LoyaltyCard;
